import { useMutation } from '@apollo/client'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Button,
  ButtonColor,
  NotificationType,
  TextField,
} from '../../../components'
import { UPDATE_CREATOR, UpdateCreatorResponse } from '../../../gql'
import {
  Creator,
  CreatorOnboardingStep,
  MutationUpdateCreatorArgs,
  UpdateCreatorInput,
} from '../../../gql/types'
import { AnalyticsEvent, trackEvent } from '../../../utils/analytics'
import { useNotification } from '../../../utils/hooks'
import { Steps } from '../components/Steps'
import { Bullets } from '../components/Bullets'
import {
  validateAndCleanSocialMediaInput,
  validateAndCleanOtherSocialMediaLink,
} from '../../../utils/helpers'

export type SetSocialMediaFormData = {
  instagramHandle: string
  tiktokHandle: string
  canvaLink: string
  otherSocialMediaLink: string
}

interface Props {
  creator: Creator
}

const SetSocialMedia = ({ creator }: Props) => {
  const { addNotification } = useNotification()
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})

  const [updateCreator, { loading }] = useMutation<
    UpdateCreatorResponse,
    MutationUpdateCreatorArgs
  >(UPDATE_CREATOR)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetSocialMediaFormData>({
    defaultValues: {
      instagramHandle: creator?.instagramHandle ?? '',
      tiktokHandle: creator?.tiktokHandle ?? '',
      canvaLink: creator?.canvaLink ?? '',
      otherSocialMediaLink: creator?.otherSocialMediaLink ?? '',
    },
    mode: 'onSubmit',
  })

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewCreatorsOnboardingSetProfile)
  }, [])

  const goBack = async () => {
    try {
      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            onboarding: CreatorOnboardingStep.setPaypalEmailAddress,
          } as UpdateCreatorInput,
        },
      })
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  const onSubmit = async (data?: SetSocialMediaFormData) => {
    try {
      if (!data) return

      // Reset errors
      setValidationErrors({})

      // Instagram
      const instagramResult = validateAndCleanSocialMediaInput(
        data.instagramHandle,
        'instagram',
      )
      if (!instagramResult.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          instagramHandle: instagramResult.error,
        }))
        return
      }

      // TikTok
      const tiktokResult = validateAndCleanSocialMediaInput(
        data.tiktokHandle,
        'tiktok',
      )
      if (!tiktokResult.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          tiktokHandle: tiktokResult.error,
        }))
        return
      }

      // Canva
      const canvaResult = validateAndCleanOtherSocialMediaLink(data.canvaLink)
      if (!canvaResult.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          canvaLink: canvaResult.error,
        }))
        return
      }

      // Other
      const otherResult = validateAndCleanOtherSocialMediaLink(
        data.otherSocialMediaLink,
      )
      if (!otherResult.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          otherSocialMediaLink: otherResult.error,
        }))
        return
      }

      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            onboarding: CreatorOnboardingStep.agreeTerms,
            instagramHandle: instagramResult.cleanedValue,
            tiktokHandle: tiktokResult.cleanedValue,
            canvaLink: canvaResult.cleanedValue,
            otherSocialMediaLink: otherResult.cleanedValue,
          } as UpdateCreatorInput,
        },
      })
    } catch (error) {
      console.error('Error in SetSocialMedia:', error?.message)
      addNotification(error?.message, NotificationType.error)
    }
  }

  const SocialMediaFields = () => {
    return (
      <div className='grid grid-cols-6 gap-6'>
        <div className='col-span-6'>
          <TextField
            label='Instagram Handle or Link'
            name='instagramHandle'
            placeholder='@yourusername or instagram.com/yourusername'
            error={validationErrors.instagramHandle}
            {...register('instagramHandle', {
              maxLength: {
                value: 500,
                message: 'The Instagram username or URL is too long',
              },
            })}
          />
        </div>

        <div className='col-span-6'>
          <TextField
            label='TikTok Handle or Link'
            name='tiktokHandle'
            placeholder='@yourusername or tiktok.com/@yourusername'
            error={validationErrors.tiktokHandle}
            {...register('tiktokHandle', {
              maxLength: {
                value: 500,
                message: 'The TikTok username or URL is too long',
              },
            })}
          />
        </div>

        <div className='col-span-6'>
          <TextField
            label='Canva Portfolio Link'
            name='canvaLink'
            placeholder='https://username.my.canva.site/portfolio-name'
            error={validationErrors.canvaLink}
            {...register('canvaLink', {
              maxLength: {
                value: 1000,
                message: 'The URL is too long',
              },
            })}
          />
        </div>

        <div className='col-span-6'>
          <TextField
            label='Other Social Media Link'
            name='otherSocialMediaLink'
            placeholder='https://www.facebook.com/yourprofile'
            error={validationErrors.otherSocialMediaLink}
            {...register('otherSocialMediaLink', {
              maxLength: {
                value: 1000,
                message: 'The URL is too long',
              },
            })}
          />
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-screen flex flex-col'>
      <div className='flex-grow'>
        <Steps
          creator={creator}
          stepCurrent={CreatorOnboardingStep.setSocialMedia}
        />

        <div className='min-h-full flex flex-col justify-center pt-4 px-4 md:mt-8 lg:px-8'>
          <div className='sm:mx-auto sm:w-full sm:max-w-2xl'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='md:shadow rounded-t-md sm:overflow-hidden mb-8 md:bg-white'>
                <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                  <div className='sm:mx-auto'>
                    <h2 className='mt-0 md:text-center text-3xl font-extrabold text-gray-900'>
                      Social Media Handles
                    </h2>
                    <p className='mt-2 md:text-center text-sm text-gray-600'>
                      This information is for internal use only and won't be
                      shared with brands.
                    </p>
                  </div>

                  <SocialMediaFields />
                </div>

                <p
                  className='text-sm my-4 text-center cursor-pointer text-gray-500 hover:text-gray-700'
                  onClick={() => onSubmit()}
                >
                  I'll add this later
                </p>

                <div className='flex justify-between md:shadow rounded-b-md py-3 md:bg-gray-50 text-right sm:px-6'>
                  <Button
                    title='Go back'
                    type='button'
                    colour={ButtonColor.white}
                    loading={loading}
                    disabled={loading}
                    onClick={goBack}
                  />
                  <Button
                    title='Continue'
                    type='submit'
                    loading={loading}
                    disabled={loading}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Bullets stepCurrent={CreatorOnboardingStep.setSocialMedia} />
    </div>
  )
}

export default SetSocialMedia
